/**
 * Business outcome chosen during onboarding (UnifiedOnboarding).
 * Distinct from CreateExperiment measurement goals (form_submissions, etc.).
 *
 * `express` is a legacy id kept so old localStorage / track() payloads still
 * parse. It is no longer a selectable first-screen skip.
 */
export type OnboardingGoalId =
  | "saas"
  | "ecommerce"
  | "newsletter"
  | "agency"
  | "express"

export const SELECTABLE_ONBOARDING_GOALS: Exclude<OnboardingGoalId, "express">[] = [
  "saas",
  "ecommerce",
  "newsletter",
  "agency",
]

export const ONBOARDING_GOAL_LABELS: Record<OnboardingGoalId, string> = {
  saas: "More trials and customers",
  ecommerce: "More store sales",
  newsletter: "More subscribers",
  agency: "Results for clients",
  express: "Just explore",
}

export const ONBOARDING_GOAL_DESCRIPTIONS: Record<OnboardingGoalId, string> = {
  saas: "First tests on signup, pricing and trial pages.",
  ecommerce: "First tests on product pages, cart and checkout.",
  newsletter: "First tests on the subscribe path.",
  agency: "First tests on a client site, so you can show a lift.",
  express: "Explore the platform now, add a site when you're ready.",
}

/**
 * The plural noun a user of this archetype actually counts.
 *
 * Onboarding surfaces should speak in the user's own vocabulary rather than the
 * generic "conversions": a Beehiiv operator is counting subscribers, a store is
 * counting orders. Kept next to the labels so the two never drift.
 */
const ONBOARDING_GOAL_NOUNS: Record<OnboardingGoalId, string> = {
  saas: "trials",
  ecommerce: "orders",
  newsletter: "subscribers",
  agency: "conversions",
  express: "conversions",
}

export function goalNoun(goal: OnboardingGoalId | null | undefined): string {
  return goal ? ONBOARDING_GOAL_NOUNS[goal] : "conversions"
}

export function isOnboardingGoalId(value: unknown): value is OnboardingGoalId {
  return (
    typeof value === "string" &&
    (value === "saas" ||
      value === "ecommerce" ||
      value === "newsletter" ||
      value === "agency" ||
      value === "express")
  )
}

export function isSelectableOnboardingGoalId(
  value: unknown,
): value is Exclude<OnboardingGoalId, "express"> {
  return isOnboardingGoalId(value) && value !== "express"
}

/** Who is setting this up — asked before the business goal. */
export type OnboardingIcpId = "marketing_team" | "founder" | "agency" | "creator"

export const ONBOARDING_ICP_LABELS: Record<OnboardingIcpId, string> = {
  marketing_team: "In-house marketing team",
  founder: "Founder or operator",
  agency: "Agency",
  creator: "Newsletter or creator",
}

export const ONBOARDING_ICP_DESCRIPTIONS: Record<OnboardingIcpId, string> = {
  marketing_team: "We run growth for our own product.",
  founder: "I'm setting this up myself.",
  agency: "I manage sites for clients.",
  creator: "I grow a list or audience.",
}

export function isOnboardingIcpId(value: unknown): value is OnboardingIcpId {
  return (
    typeof value === "string" &&
    (value === "marketing_team" ||
      value === "founder" ||
      value === "agency" ||
      value === "creator")
  )
}
